import type { HeroId } from "@rivalwatch/shared";
import type { Out } from "./engine.ts";
import { CLIENT_HEROES } from "../heroes/index.ts";
import { bell, brass, click, cymbal, drive, gainOut, type Instrument, later, noise, sub, thump, timpani, tuba } from "./synth.ts";

/**
 * Harmony is team colour. Everything tonal is in D:
 *   ally     D Ionian   — bright, heroic (the HUD's blue)
 *   foe      D Phrygian — the flat second is the menace (red)
 *   neutral  open fifths, no third — the objective before anyone owns it
 *   ult      D Lydian   — the raised fourth shimmers (gold)
 *   heal     D major pentatonic — nothing in it can clash (green)
 */
export const D4 = 293.66;
export const hz = (semi: number, base = D4) => base * 2 ** (semi / 12);

export const SCALE = {
  ally: [0, 2, 4, 5, 7, 9, 11],
  foe: [0, 1, 3, 5, 7, 8, 10],
  neutral: [0, 7],
  ult: [0, 2, 4, 6, 7, 9, 11],
  heal: [0, 2, 4, 7, 9],
} as const;

/** Ionian → Phrygian: the same melodic contour with 2, 3, 6 and 7 lowered. */
export const hostile = (s: number) => ([2, 4, 9, 11].includes(((s % 12) + 12) % 12) ? s - 1 : s);

/** The KO chime climbs the ally pentatonic with each chained elimination. */
export const STREAK_STEPS = [0, 2, 4, 7, 9, 12];

export type Note = [semi: number | null, beats: number];

export interface Theme {
  bpm: number;
  notes: Note[];
  /** Octave of the melody relative to D4. */
  octave: number;
  voice: Instrument;
  /** A doubling layer: glass over the choir, scrap metal under the tuba… */
  double?: (o: Out, f: number, dur: number, vel: number) => void;
}

export const THEMES = Object.fromEntries(Object.values(CLIENT_HEROES).map((h) => [h.id, h.audio.theme])) as Record<HeroId, Theme>;

export type ThemeMode = "heroic" | "hostile" | "tease";

/**
 * A hero's theme as a stinger.
 *   heroic  — you and your allies: Ionian, a glass shimmer on the downbeat, a warm brass bed.
 *   hostile — enemies: Phrygian, an octave down, driven hard over an impact and a low cluster.
 *   tease   — the first three notes, soft (ultimate ready, hero select).
 */
export function playTheme(outer: Out, hero: HeroId, mode: ThemeMode): number {
  const h = CLIENT_HEROES[hero];
  const o = gainOut(outer, 10 ** (h.audio.themeTrim[mode] / 20));
  const th = h.audio.theme;
  const beat = 60 / th.bpm;
  const map = mode === "hostile" ? hostile : (s: number) => s;
  const oct = mode === "hostile" && th.octave >= 0 ? th.octave - 1 : th.octave;
  const notes = mode === "tease" ? th.notes.slice(0, 3) : th.notes;
  const vel = mode === "tease" ? 0.55 : mode === "hostile" ? 1 : 0.85;
  const melody = mode === "hostile" ? drive(gainOut(o, 0.75), 2.6) : o;
  let at = mode === "tease" ? 0 : 0.05;

  if (mode === "heroic") {
    bell(o, hz(24), 0.4, 0.45);
    bell(later(o, 0.012), hz(31), 0.4, 0.4);
    cymbal(o, 0.45, 1.3);
    thump(o, 0.3, 110, 55, 0.25);
  } else if (mode === "hostile") {
    thump(o, 0.95, 72, 28, 0.6);
    click(o, 0.5, 1600, 0.014);
    noise(o, { color: "pink", type: "lowpass", f: 1600, f1: 180, gain: 0.5, d: 0.55 });
    cymbal(o, 0.55, 1.1);
  }

  notes.forEach(([s, beats], i) => {
    const dur = beats * beat;
    if (s !== null) {
      const f = hz(map(s) + oct * 12);
      const last = i === notes.length - 1;
      th.voice(later(melody, at), f, dur * (last ? 1.5 : 0.92), vel);
      th.double?.(later(o, at), f, dur, vel);
    }
    at += dur;
  });

  if (mode !== "tease") {
    const bed = mode === "heroic" ? [-12, -8, -5] : [-24, -17, -11, -9];
    const bedOut = gainOut(o, mode === "heroic" ? 0.4 : 0.55);
    for (const s of bed) (mode === "heroic" ? brass : tuba)(later(bedOut, 0.03), hz(s), at, 0.65);
    sub(later(o, 0.02), hz(-24), at, mode === "heroic" ? 0.45 : 0.7);
    if (mode === "hostile") timpani(later(o, Math.max(0, at - beat * 0.5)), hz(-24), 0.9);
  }
  return at + (mode === "tease" ? 0.9 : 1.4);
}

/** A chord stab (objective captured or lost, round results). */
export function stab(o: Out, chord: number[], vel = 0.9, dur = 0.45, voice: Instrument = brass) {
  let end = 0;
  for (const s of chord) end = Math.max(end, voice(o, hz(s), dur, vel));
  return end;
}
